import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject, Subscription } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';

import { RoutingService } from '../../../services/routing.service';
import { $sso, CHAR_LIMITS } from '../../../../../shared/constants/sso';
import { CustomValidators } from '../../../services/helpers/form-custom-validators';
import { Api } from '../../../../../shared/constants/api';
import { ErrorMessageService } from '../../../services/error-message.service';
import { ITenant, ITenantSsoConfig } from '../../../../../shared/interfaces/tenant.interface';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { TenantService } from '../../../services/tenant.service';
import { SharedCommonUtility } from '../../../../../shared/utils/common.utility';
import { $tenant } from '../../../../../shared/constants/tenant';
import { NotificationService } from '../../../services/notification.service';
import { NotificationPosition } from '../../../models/notification.model';
import { ModalService } from '../../../services/modal.service';
import { TenantRemoveSsoModalComponent } from './tenant-remove-sso-modal/tenant-remove-sso-modal.component';
import { LoadErrorHandler } from '../../../services/load-error-handler';
import { TenantRouteUtil } from '../tenant-route.util';

@Component({
  selector: 'app-tenant-configure-sso',
  styleUrls: ['tenant-configure-sso.component.scss'],
  templateUrl: './tenant-configure-sso.component.html',
  standalone: false,
})
export class TenantConfigureSsoComponent implements OnInit, OnDestroy {
  private subscription: Subscription = new Subscription();
  private tenantId: string = '';
  private tenantName: string = '';

  public pageTitle: string;
  public hasExistingConfig: boolean = false;

  public form: UntypedFormGroup;
  public formValidationRequest$: Subject<void> = new Subject<void>();

  public $sso: typeof $sso = $sso;
  public Api: typeof Api = Api;
  public CHAR_LIMITS: typeof CHAR_LIMITS = CHAR_LIMITS;
  public tenantRoutePath: string;

  constructor(
    private routingService: RoutingService,
    private formBuilder: UntypedFormBuilder,
    private element: ElementRef<Element>,
    private errorMessageService: ErrorMessageService,
    private router: Router,
    private errorHandlerService: ErrorHandlerService,
    private tenantService: TenantService,
    private activatedRoute: ActivatedRoute,
    private notificationService: NotificationService,
    private modalService: ModalService,
    private loadErrorHandler: LoadErrorHandler,
  ) {
    this.form = this.createForm();
    this.tenantRoutePath = TenantRouteUtil.getTenantRoutePath(this.router.url);
  }

  private createForm(): UntypedFormGroup {
    const config: Partial<Record<$sso, UntypedFormControl>> = {
      [$sso.idpDomain]: new UntypedFormControl(null, [
        CustomValidators.required,
        Validators.maxLength(CHAR_LIMITS[$sso.idpDomain]),
        CustomValidators.isValidUrl,
      ]),
      [$sso.idpClientId]: new UntypedFormControl(null, [
        CustomValidators.required,
        Validators.maxLength(CHAR_LIMITS[$sso.idpClientId]),
      ]),
      [$sso.audience]: new UntypedFormControl(null, [
        CustomValidators.required,
        Validators.maxLength(CHAR_LIMITS[$sso.audience]),
        CustomValidators.isValidUrl,
      ]),
    };

    return this.formBuilder.group(config);
  }

  private populateForm(ssoConfig: ITenantSsoConfig): void {
    this.form.get($sso.idpClientId).setValue(ssoConfig[$sso.idpClientId]);
    this.form.get($sso.idpDomain).setValue(ssoConfig[$sso.idpDomain]);
    this.form.get($sso.audience).setValue(ssoConfig[$sso.audience]);
  }

  public async save(): Promise<void> {
    this.formValidationRequest$.next();

    if (!this.form.valid) {
      this.errorMessageService.setFocusOnFirstError(this.element.nativeElement);
      return;
    }

    const ssoConfig: ITenantSsoConfig = {
      [$sso.idpClientId]: this.form.get($sso.idpClientId).value?.trim(),
      [$sso.idpDomain]: this.form.get($sso.idpDomain).value?.trim(),
      [$sso.audience]: this.form.get($sso.audience).value?.trim(),
    };

    this.subscription.add(
      this.tenantService.setSsoConfig(this.tenantId, ssoConfig).subscribe({
        next: () => {
          this.router
            .navigate([this.tenantRoutePath])
            .catch(this.errorHandlerService.handleRoutingError.bind(this.errorHandlerService));
        },
        error: () => {
          this.notificationService.error('set_sso_config_error', NotificationPosition.Toast);
        },
      }),
    );
  }

  public removeSso(): void {
    const modal: TenantRemoveSsoModalComponent = this.modalService.open(TenantRemoveSsoModalComponent).instance;
    modal.tenantName = this.tenantName;

    const onSuccess = (): void => {
      this.router
        .navigate([this.tenantRoutePath])
        .catch(this.errorHandlerService.handleRoutingError.bind(this.errorHandlerService));
    };

    const onError = (error: HttpErrorResponse): void => {
      this.loadErrorHandler.handleError('error_removing_sso', error, TenantConfigureSsoComponent.name);
    };

    this.subscription.add(
      modal.confirmed
        .pipe(
          switchMap((reason: string): Observable<void> => {
            return this.tenantService.removeSsoConfig(this.tenantId, reason);
          }),
        )
        .subscribe({
          next: onSuccess.bind(this),
          error: onError.bind(this),
        }),
    );
  }

  public ngOnInit(): void {
    this.pageTitle = this.routingService.getPageTitle();
    this.tenantId = this.activatedRoute.snapshot.paramMap.get(Api.tenantId);

    this.subscription.add(
      this.tenantService
        .getTenant(this.tenantId)
        .pipe(
          tap((tenant: ITenant) => {
            this.tenantName = tenant[$tenant.name];
            if (SharedCommonUtility.isNullish(tenant[$tenant.ssoConfig])) {
              return;
            }

            this.hasExistingConfig = true;
            this.populateForm(tenant[$tenant.ssoConfig]);
          }),
        )
        .subscribe(),
    );
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
